/**
 * Status Reports Service
 *
 * Status reports are written in dim-docs, built there together with an
 * index.json generated from the front matter of each report, and uploaded to
 * website/status-reports/ on the DIM file share. nginx serves them behind login
 * under /status-reports/files/ (deploy/nginx.conf), so new reports appear
 * without a web release.
 *
 * Layout under env.statusReportsBase:
 *   index.json
 *   <yyyy-mm>/statusrapport.md          bokmål (always)
 *   <yyyy-mm>/statusrapport.<lang>.md   optional translations (en, nn)
 *   <yyyy-mm>/bilder/...
 */

import env from '@config/env';
import type {
  LocalizedStatusReport,
  StatusReportContent,
  StatusReportHighlight,
  StatusReportIndex,
  StatusReportLanguage,
  StatusReportSummary,
} from '@/types';

const PERIOD_PATTERN = /^\d{4}-(0[1-9]|1[0-2])$/;
const TRANSLATION_LANGUAGES = ['en', 'nn'] as const;

export function isValidPeriod(period: string | undefined): period is string {
  return typeof period === 'string' && PERIOD_PATTERN.test(period);
}

function baseUrl(): string {
  return env.statusReportsBase.replace(/\/+$/, '');
}

export function getReportFolderUrl(period: string): string {
  return `${baseUrl()}/${period}/`;
}

/**
 * Resolve a URL from a report (image src or link href) against the report folder.
 * Absolute URLs, root-relative paths, anchors and mailto/tel links are left alone.
 */
export function resolveReportUrl(url: string, period: string): string {
  if (!url) return url;
  if (/^([a-z][a-z0-9+.-]*:|\/|#)/i.test(url)) return url;
  const relative = url.replace(/^\.\//, '');
  return getReportFolderUrl(period) + relative.split('/').map(encodeURIComponent).join('/');
}

function isString(value: unknown): value is string {
  return typeof value === 'string' && value.trim().length > 0;
}

function parseHighlight(value: unknown): StatusReportHighlight | null {
  if (!value || typeof value !== 'object') return null;
  const { emoji, title, text } = value as Record<string, unknown>;
  if (!isString(title) || !isString(text)) return null;
  return { emoji: isString(emoji) ? emoji : '', title, text };
}

function parseContent(value: unknown): StatusReportContent | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  if (!isString(raw.title)) return null;
  return {
    title: raw.title,
    intro: isString(raw.intro) ? raw.intro.trim() : '',
    highlights: Array.isArray(raw.highlights)
      ? raw.highlights.map(parseHighlight).filter((h): h is StatusReportHighlight => h !== null)
      : [],
  };
}

function parseSummary(value: unknown): StatusReportSummary | null {
  if (!value || typeof value !== 'object') return null;
  const raw = value as Record<string, unknown>;
  const content = parseContent(raw);
  if (!isValidPeriod(raw.period as string) || !content) return null;

  // A translation counts only if it is listed in languages and has its content
  const listed = Array.isArray(raw.languages) ? raw.languages : [];
  const rawTranslations = (raw.translations && typeof raw.translations === 'object'
    ? raw.translations : {}) as Record<string, unknown>;
  const translations: StatusReportSummary['translations'] = {};
  for (const lang of TRANSLATION_LANGUAGES) {
    const translated = listed.includes(lang) ? parseContent(rawTranslations[lang]) : null;
    if (translated) translations[lang] = translated;
  }

  return {
    period: raw.period as string,
    published: isString(raw.published) ? raw.published : '',
    ...content,
    ...(isString(raw.cover) ? { cover: raw.cover } : {}),
    ...(raw.draft === true ? { draft: true } : {}),
    languages: ['nb', ...TRANSLATION_LANGUAGES.filter(lang => translations[lang])],
    translations,
  };
}

/** The report language that matches a UI language: 'en' → 'en', 'nn-NO' → 'nn', anything else → 'nb'. */
export function toReportLanguage(uiLanguage: string): StatusReportLanguage {
  const base = uiLanguage.toLowerCase().split('-')[0];
  return base === 'en' || base === 'nn' ? base : 'nb';
}

/**
 * Show a report in the UI language if it has been translated, otherwise in bokmål.
 */
export function localizeReport(report: StatusReportSummary, uiLanguage: string): LocalizedStatusReport {
  const wanted = toReportLanguage(uiLanguage);
  const translation = wanted === 'nb' ? undefined : report.translations[wanted];
  if (translation) {
    return { ...report, ...translation, lang: wanted, isFallback: false };
  }
  return { ...report, lang: 'nb', isFallback: wanted !== 'nb' };
}

/** File name of a report in a language: statusrapport.md for bokmål, statusrapport.<lang>.md otherwise. */
export function reportFilename(lang: StatusReportLanguage): string {
  return lang === 'nb' ? 'statusrapport.md' : `statusrapport.${lang}.md`;
}

/**
 * Validate and normalise index.json. Invalid entries are dropped rather than
 * failing the whole page, and reports are sorted newest first.
 */
export function parseStatusReportIndex(data: unknown): StatusReportIndex {
  const raw = (data && typeof data === 'object' ? data : {}) as Record<string, unknown>;
  const entries = Array.isArray(raw.reports) ? raw.reports : [];
  const reports = entries
    .map(parseSummary)
    .filter((r): r is StatusReportSummary => r !== null)
    .sort((a, b) => b.period.localeCompare(a.period));

  return {
    ...(isString(raw.generated) ? { generated: raw.generated } : {}),
    reports,
  };
}

/** Remove a leading YAML front matter block, if present. */
export function stripFrontMatter(markdown: string): string {
  return markdown.replace(/^\uFEFF?---\r?\n[\s\S]*?\r?\n---[ \t]*(\r?\n|$)/, '');
}

export class StatusReportNotFoundError extends Error {
  constructor(period: string) {
    super(`Status report not found: ${period}`);
    this.name = 'StatusReportNotFoundError';
  }
}

async function fetchFromShare(url: string): Promise<Response> {
  // Always revalidate (nginx also sends Cache-Control: no-cache), so a newly
  // published report shows up at once.
  return fetch(url, { credentials: 'include', cache: 'no-cache' });
}

export async function getStatusReportIndex(): Promise<StatusReportIndex> {
  const response = await fetchFromShare(`${baseUrl()}/index.json`);
  if (response.status === 404) {
    return { reports: [] };
  }
  if (!response.ok) {
    throw new Error(`Failed to load status report index: ${response.status} ${response.statusText}`);
  }
  return parseStatusReportIndex(await response.json());
}

export async function getStatusReportMarkdown(period: string, lang: StatusReportLanguage = 'nb'): Promise<string> {
  if (!isValidPeriod(period)) {
    throw new StatusReportNotFoundError(period);
  }
  const response = await fetchFromShare(`${getReportFolderUrl(period)}${reportFilename(lang)}`);
  if (response.status === 404) {
    throw new StatusReportNotFoundError(period);
  }
  if (!response.ok) {
    throw new Error(`Failed to load status report ${period}: ${response.status} ${response.statusText}`);
  }
  return stripFrontMatter(await response.text());
}

/** "2026-10" → "oktober 2026" (or "October 2026" in English). */
export function formatPeriod(period: string, locale: string): string {
  const [year, month] = period.split('-').map(Number);
  if (!year || !month) return period;
  return new Intl.DateTimeFormat(locale, { month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, 1)));
}

/** "2026-11-04" → "4. november 2026". Returns the input unchanged if it is not a date. */
export function formatPublishedDate(date: string, locale: string): string {
  const match = /^(\d{4})-(\d{2})-(\d{2})$/.exec(date);
  if (!match) return date;
  const [, year, month, day] = match.map(Number);
  return new Intl.DateTimeFormat(locale, { day: 'numeric', month: 'long', year: 'numeric', timeZone: 'UTC' })
    .format(new Date(Date.UTC(year, month - 1, day)));
}

export default {
  getStatusReportIndex,
  getStatusReportMarkdown,
  parseStatusReportIndex,
  stripFrontMatter,
  toReportLanguage,
  localizeReport,
  reportFilename,
  resolveReportUrl,
  getReportFolderUrl,
  formatPeriod,
  formatPublishedDate,
  isValidPeriod,
};
